import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { forEachFrame, readFrame } from "./parseFrame";
import { getPositionIdentity } from "../../utils/positionIdentity";
import { subscribeTradeExecuted } from "../../utils/tradeEvents";
import {
  classifyRows,
  clearAccount as clearPendingCloseAccount,
  getSnapshot as getPendingClosesSnapshot,
  observeRows as observePendingCloseRows,
  openPendingClose,
  SETTLING_FLAG,
  releasePendingClosesFor,
  subscribe as subscribePendingCloses,
} from "../../services/tradeSync";
import { useGetAccount } from "../api/account/useGetAccount";
import { IWSSPositionResponse } from "./model/position";
import { GetAccount200ResponsePositionsInner } from "../../codegen-api";
import {
  normalizeDecimalString,
  toPlainDecimalString,
} from "../../utils/decimalString";
import { IPositionMap } from "./usePositionsWSS.types";
import {
  positionChannel,
  getPositionKey,
  transformPositionsToMap,
  isNormalizedPositionPatch,
  normalizePositionIdentity,
  getPositionCandidateKeys,
  getPositionEventAction,
  isClosedPositionAction,
  isClosedPositionPatch,
  getMessageAccount,
  hasMaterialPositionChange,
} from "./usePositionsWSS.helpers";

export * from "./usePositionsWSS.types";
export * from "./usePositionsWSS.helpers";

/**
 * Project a complete set of server rows through the shared pending-close
 * registry: drop the rows a confirmed close provably removed, and MARK the ones
 * that could be the closed position but are not provably the only candidate.
 *
 * Both callers below hand in a complete set (`/account` and a websocket
 * snapshot), which is what makes uniqueness decidable here.
 *
 * The marking half is what reaches mobile. The paginated `/positions` table
 * tags its own rows the same way (see useCursorPaginatedHistory), but the mobile
 * bottom bar renders `MobilePositionsTab`, which reads THIS hook — so without
 * this the flag existed on desktop only and mobile silently presented a row with
 * a close in flight as an ordinary live position.
 */
const projectRows = (
  account: string | undefined | null,
  rows: GetAccount200ResponsePositionsInner[]
): GetAccount200ResponsePositionsInner[] => {
  const { hidden, settling } = classifyRows(account || undefined, rows, true);
  const hiddenIndices = new Set(hidden);
  const settlingIndices = new Set(settling);
  // Tag against the ORIGINAL indices, then drop hidden rows — filtering first
  // would shift every index out from under the settling set.
  return rows
    .map((row, index) =>
      settlingIndices.has(index) ? { ...row, [SETTLING_FLAG]: true } : row
    )
    .filter((_, index) => !hiddenIndices.has(index));
};

/**
 * A patch's position size, unsigned, as plain decimal text.
 *
 * `String(Math.abs(Number(amount)))` wrote a 0.0000005 position as "5e-7",
 * which a full limit close then fed to `parseUnits` (it throws), and pushed an
 * 18-decimal size through a float that can round it UP past what is held. The
 * server's own string is kept digit for digit; only a non-numeric string falls
 * back to the float.
 */
export const toAbsoluteAmountString = (amount: unknown): string => {
  const plain =
    typeof amount === "string" || typeof amount === "number"
      ? normalizeDecimalString(amount)
      : null;
  if (plain !== null) return plain.replace(/^-/, "");
  return toPlainDecimalString(Math.abs(Number(amount)));
};

const usePositionsWSS = () => {
  const { account } = useContext(AuthContext);
  const {
    data: accountData,
    isValidating,
    mutate: mutateAccount,
    error: accountError,
  } = useGetAccount();
  const {
    authenticated,
    triggerSubscribe,
    triggerUnsubscribe,
    registerRevalidate,
    lastMessages,
  } = useContext(WebsocketContext);
  // Positions are read from `/account`; refetch it when the positions snapshot
  // failed server-side. Through a ref so a new `mutate` never re-subscribes.
  const mutateAccountRef = useRef(mutateAccount);
  mutateAccountRef.current = mutateAccount;

  const [positionsMap, setPositionsMap] = useState<IPositionMap>({});
  const [hasLoadedPositions, setHasLoadedPositions] = useState(false);

  const loading = isValidating && !hasLoadedPositions;

  useEffect(() => {
    setPositionsMap({});
    setHasLoadedPositions(false);
  }, [account]);

  // In case websocket is slow,
  // If account data positions is newer, update positionsMap
  // Let the registry see what `/account` actually returned: this is what
  // releases a pending close whose row proves to be a different position (a
  // re-open) and what ages one the server keeps contradicting.
  useEffect(() => {
    if (!accountData || !account) return;
    observePendingCloseRows(account, accountData.positions || [], true);
  }, [account, accountData]);

  useEffect(() => {
    if (!accountData) {
      return;
    }

    setHasLoadedPositions(true);
    setPositionsMap((prev) => {
      // Server truth, stored RAW. Hiding locally-closed rows is a view concern
      // and happens at read time below — see the projection near `positions`.
      const livePositions = accountData.positions || [];

      if (!livePositions.length) {
        return {};
      }

      // If previously empty, just set positions.
      if (!prev) {
        return transformPositionsToMap(livePositions);
      }

      // If accountData.positions is different than positionsMap (new positions), just set map to accountData
      // We do this by checking the list of position keys, eg "0x123-buy,0x123-sell"
      const isDifferentInstruments =
        Object.keys(prev).sort().join(",") !==
        livePositions.map((p) => getPositionKey(p)).sort().join(",");
      if (isDifferentInstruments) {
        return transformPositionsToMap(livePositions);
      }

      // Now we look for other notable props to update when positions from accounts api changes
      for (let i = 0; i < (livePositions.length || 0); i += 1) {
        const p = livePositions[i];
        const existingPositionFromMap = prev[getPositionKey(p)];
        if (!existingPositionFromMap) {
          return transformPositionsToMap(livePositions);
        }

        // TP/SL, margin, and (FE-5 interim) size, entry and liquidation price:
        // a quiet socket must not leave the trade form sizing a reduce-only
        // close from a stale row.
        if (hasMaterialPositionChange(existingPositionFromMap, p)) {
          return transformPositionsToMap(livePositions);
        }
      }

      // No change
      return prev;
    });
    // `account` is read through `classifyRows` above, so a wallet switch has to
    // re-project this map rather than keep the previous account’s verdict.
  }, [account, accountData]);

  useEffect(() => {
    if (!authenticated || !account) {
      return undefined;
    }

    const channels = [`${positionChannel}:${account.toLowerCase()}`];
    triggerSubscribe("subscribe", channels);
    const unregister = registerRevalidate?.(channels, () => {
      void mutateAccountRef.current?.();
    });
    // Release on unmount, account change or de-auth (M1).
    return () => {
      unregister?.();
      triggerUnsubscribe(channels);
    };
  }, [account, authenticated, registerRevalidate, triggerSubscribe, triggerUnsubscribe]);

  // Record a close the trader just submitted in the shared registry. The
  // `accountData` effect above projects it out of this map, and the paginated
  // table projects the same entry out of its own rows, so both views agree.
  //
  // Nothing here expires on a timer. A confirmed close is not proof the
  // position is gone — the router turns it into a reduce-only market order, and
  // the gasless path never waits for the chain at all — so the registry
  // releases the entry only on positive evidence, and stops hiding after enough
  // contradicting responses rather than hiding a live position indefinitely.
  useEffect(() => {
    if (!account) return undefined;
    return subscribeTradeExecuted(
      ({ closedPosition, closeProof, openedPosition }) => {
        if (openedPosition) {
          // Our own confirmed open on this market and side supersedes any
          // pending close for it. Without this, re-opening at exactly the same
          // size and entry price would look identical to the stale closed row
          // and stay hidden.
          releasePendingClosesFor(account, openedPosition);
        }
        if (!closedPosition) return;
        openPendingClose({
          account,
          position: closedPosition,
          proof: closeProof || "broadcast",
        });
      }
    );
  }, [account]);

  // A wallet switch must not carry one account's unconfirmed closes into
  // another's data.
  //
  // This clears on an account CHANGE, never on unmount. `usePositionsWSS` is
  // mounted at nine sites (three or four at once on the perps page), and an
  // unmount-time cleanup is not ref-counted: any one of them unmounting —
  // navigating to Portfolio, switching perps/options — wiped every pending
  // close for the account and un-hid positions the trader had just closed.
  const previousAccountRef = useRef(account);
  useEffect(() => {
    const previousAccount = previousAccountRef.current;
    if (previousAccount && previousAccount !== account) {
      clearPendingCloseAccount(previousAccount);
    }
    previousAccountRef.current = account;
  }, [account]);

  // Receives messages and updates state
  useEffect(() => {
    if (lastMessages) {
      forEachFrame(lastMessages, "usePositionsWSS", (lastMessage) => {
        const parsed = readFrame<IWSSPositionResponse>(lastMessage);
        if (!parsed) return;
        const { data, channel } = parsed;

        const normalizedChannel = String(channel || "").toLowerCase();
        const normalizedAccount = account?.toLowerCase();
        const channelName = normalizedChannel.split(":")[0];
        const messageAccount = getMessageAccount(parsed);
        const isOwnAccountEvent =
          !!normalizedAccount &&
          (normalizedChannel === `${positionChannel}:${normalizedAccount}` ||
            messageAccount === normalizedAccount);

        if (!isOwnAccountEvent) {
          return;
        }

        const positionsSnapshot = Array.isArray(data?.positions)
          ? data.positions
          : undefined;
        const positionPatch = data?.position;
        const action = getPositionEventAction(data);

        if (channelName === positionChannel && positionsSnapshot) {
          setHasLoadedPositions(true);
          // A websocket snapshot is the complete position set, so uniqueness is
          // decidable — let the registry see it. The snapshot itself is stored
          // RAW; hiding happens at read time, through the SAME registry the
          // paginated table uses.
          observePendingCloseRows(account || undefined, positionsSnapshot, true);
          setPositionsMap(transformPositionsToMap(positionsSnapshot));
          return;
        }

        if (channelName === positionChannel && positionPatch) {
          setHasLoadedPositions(true);
          const candidates = getPositionCandidateKeys(positionPatch);
          // A close arrives either as an explicit close/liquidate `action`, or as a
          // terminal-status / zero-size patch (raw indexer liquidation events carry
          // status LIQUIDATED + sizeInTokens 0 with no action). Either one removes the
          // row + tombstones the key so a lagging account-API snapshot can't resurrect it.
          if (isClosedPositionAction(action) || isClosedPositionPatch(positionPatch)) {
            // Reported by the server, so this is receipt-grade evidence —
            // stronger than a close we merely submitted.
            if (account) {
              openPendingClose({
                account,
                position: positionPatch,
                proof: "receipt",
              });
            }
            setPositionsMap((prevPositions) => {
              const newPositions = { ...prevPositions };
              candidates.forEach((key) => {
                delete newPositions[key];
              });
              return newPositions;
            });
            return;
          }

          // A live patch is the server saying this position still exists, which
          // supersedes any pending close for it.
          releasePendingClosesFor(account || undefined, positionPatch);

          // Raw indexer position events are intentionally published to the
          // same account channel as an invalidation signal. They use on-chain
          // raw fields (`sizeInTokens`, `sizeInUsd`) and should not be merged
          // directly into the table state, which expects account API fields.
          if (!isNormalizedPositionPatch(positionPatch)) {
            return;
          }
          setPositionsMap((prevPositions) => {
            const newPositions = { ...prevPositions };

            const pos = normalizePositionIdentity(
              positionPatch
            ) as GetAccount200ResponsePositionsInner;
            const size = Number(pos.amount);
            if (!size) {
              getPositionCandidateKeys(pos).forEach((key) => {
                delete newPositions[key];
              });
            } else {
              // Resolve the row this patch refers to by IDENTITY, not by an
              // exact key. `getPositionKey(patch)` cannot equal the stored key
              // unless the patch happens to carry the same id-bearing field:
              // REST rows are keyed by the server's `position_key`, so a patch
              // without one produced a brand-new `…:unknown-margin` key and a
              // duplicate orphan row instead of merging. That orphan sorts
              // first, wins the instrument_name slot the trade form reads, and
              // — carrying no margin_type — made a reduce-only close throw.
              const patchKeys = new Set(getPositionCandidateKeys(pos));
              let existingKey = Object.keys(newPositions).find((candidate) =>
                getPositionCandidateKeys(newPositions[candidate]).some((k) =>
                  patchKeys.has(k)
                )
              );
              if (!existingKey && pos.instrument_name) {
                // A patch carrying only an instrument name shares no identity
                // candidate with the stored row. Fall back to the name, but ONLY
                // when it names exactly one row — a trader can hold a long and a
                // short (or a cross and an isolated leg) on the same instrument,
                // and merging into the wrong one is worse than not merging.
                const named = Object.keys(newPositions).filter(
                  (candidate) =>
                    newPositions[candidate]?.instrument_name === pos.instrument_name
                );
                if (named.length === 1) [existingKey] = named;
              }
              const key = existingKey || getPositionKey(pos);
              // MERGE, never replace. A patch carries only the fields that
              // changed, and `normalizePositionIdentity` fills the rest with
              // `undefined` — so assigning it wholesale erased everything the
              // REST snapshot knew. `margin_type` is the dangerous one: the
              // close path resolves the on-chain margin mode from it and now
              // refuses to submit when it is unknown, so a patch that wiped it
              // would leave the trader unable to close the position at all.
              // Dropping undefined keys means a patch can only ever add or
              // update, never blank out.
              const patch = Object.fromEntries(
                Object.entries(pos).filter(([, value]) => value !== undefined)
              ) as Partial<GetAccount200ResponsePositionsInner>;
              newPositions[key] = {
                ...(newPositions[key] || {}),
                ...patch,
                amount: toAbsoluteAmountString(pos.amount),
              } as GetAccount200ResponsePositionsInner;
            }

            return newPositions;
          });
        }
      });
    }
  }, [account, lastMessages]);

  /**
   * Read-time projection over raw server rows.
   *
   * The hiding used to be baked into `setPositionsMap` at write time, which made
   * the stored map a mix of server truth and a verdict computed once, at the
   * instant that write happened. Nothing recomputed it afterwards. So when the
   * registry changed on its own — a close submitted from elsewhere on the page,
   * a pending close released or retired — and no fresh `/account` response or
   * websocket message happened to arrive, this hook kept serving the stale
   * verdict. The paginated `/positions` table projects at read time and would
   * move; the mobile bottom bar, which reads THIS hook, would not, and the same
   * screen disagreed with itself about whether a position existed.
   *
   * Subscribing to the registry is what closes that gap: a registry change is
   * now a render trigger in its own right, exactly as in
   * useCursorPaginatedHistory.
   */
  const pendingCloseVersion = useSyncExternalStore(
    subscribePendingCloses,
    getPendingClosesSnapshot,
    getPendingClosesSnapshot
  );

  const positions = useMemo(
    () =>
      projectRows(
        account,
        Object.values(positionsMap || {}).sort(
          (a, b) =>
            (a.instrument_name || "").localeCompare(b.instrument_name || "") ||
            String(a.side || "").localeCompare(String(b.side || "")) ||
            String(a.margin_type || "").localeCompare(
              String(b.margin_type || "")
            )
        )
      ),
    // `account` and the registry version both feed `classifyRows`, so a wallet
    // switch or a registry change has to re-project rather than reuse a verdict
    // computed for the previous one.
    [positionsMap, account, pendingCloseVersion]
  );

  const legacyPositionsMap = useMemo(
    () =>
      positions.reduce((acc, position) => {
        const identity = getPositionIdentity(position);
        if (identity) {
          acc[identity] = position;
        }
        if (position.instrument_name && !acc[position.instrument_name]) {
          acc[position.instrument_name] = position;
        }
        return acc;
      }, {} as IPositionMap),
    [positions]
  );

  // Rebuilt from the PROJECTED list, not from the raw stored map. Returning the
  // raw map here would leak rows that `positions` hides, so the same hook would
  // contradict itself depending on which field a component happened to read.
  const positionsByKey = useMemo(
    () => transformPositionsToMap(positions),
    [positions]
  );

  return {
    positionsMap: legacyPositionsMap,
    positionsByKey,
    positions,
    loading,
    /**
     * The last `/account` read failed. A failed read ends `loading` with no
     * rows, which is indistinguishable from "no positions" unless the caller
     * checks this: an empty `positions` with `error` set is UNKNOWN, not empty.
     */
    error: accountError as Error | undefined,
  };
};

export default usePositionsWSS;
